import { z } from "zod";
import type { Address } from "../order.js";
import { leitcode, parseHouseNumber, sectionContains, splitStreet, type HouseNumber, type StreetSection } from "./housenumber.js";
import { citySimilarity, normalizeCity, normalizeStreet, streetSimilarity } from "./text.js";

/**
 * Adressprüfung gegen die DHL-Leitdaten (DataFactory): Straße, Hausnummernbereich, PLZ und Ort
 * so korrigieren, dass ein gültiger Leitcode entsteht – sonst mit Begründung zur manuellen Prüfung.
 * Packstation/Postfiliale: Nummer gegen das Verzeichnis, Postnummer vorhanden.
 */

/** Straße innerhalb einer PLZ, wie sie die Leitdaten führen */
export interface RefStreet {
  /** bundesweiter Straßenschlüssel (Namensschlüssel + laufende Nummer) */
  key: string;
  /** offizielle Schreibweise, z. B. "Niederforstbacher Str." */
  name: string;
  /** Bestimmungsort (Alphanummer) */
  alort: string;
  /** G = gültig; S/N/W = archiviert */
  status: "G" | "S" | "N" | "W";
  sections: StreetSection[];
  /** bei archivierten Straßen: neuer Straßenschlüssel */
  redirectKey?: string;
}

export interface RefLocality {
  plz: string;
  alort: string;
  /** Ortsname (PLZ-Datei) oder Ortsteil */
  name: string;
  /** offizieller Ortsname zur PLZ */
  officialName: string;
  kind: "place" | "district";
}

/** Zugriff auf die Leitdaten (Worker: aus der Datenbank mit Cache; Tests: im Speicher) */
export interface AddressReference {
  version: string;
  streets(plz: string): Promise<RefStreet[] | null>;
  /** PLZ mit Ort/Ortsteilen dieses Namens (normalisiert, auch ähnlich) */
  localitiesByCity(city: string): Promise<RefLocality[]>;
  /** Orte/Ortsteile einer PLZ */
  localitiesByPlz(plz: string): Promise<RefLocality[]>;
  packstationPlz(number: number): Promise<string[]>;
  filialePlz(number: number): Promise<string[]>;
}

export const ADDRESS_ISSUES = {
  house_number_missing: "Hausnummer fehlt",
  house_number_unknown: "Hausnummer liegt in keinem bekannten Bereich der Straße",
  street_unknown: "Straße in dieser PLZ / diesem Ort nicht gefunden",
  street_ambiguous: "Straße nicht eindeutig – mehrere ähnliche Treffer",
  zip_unknown: "PLZ unbekannt",
  city_mismatch: "Ort passt nicht zur PLZ",
  packstation_number_missing: "Packstation-Nummer fehlt",
  packstation_unknown: "Packstation nicht gefunden",
  postnummer_missing: "Postnummer fehlt (für Packstation/Postfiliale Pflicht)",
  filiale_unknown: "Postfiliale nicht gefunden",
} as const;
export type AddressIssueCode = keyof typeof ADDRESS_ISSUES;

export interface AddressCheckResult {
  status: "ok" | "corrected" | "manual" | "skipped";
  kind: "street" | "packstation" | "postfiliale";
  /** korrigierte Adresse (nur bei "corrected"/"ok") */
  address?: Address;
  changes: { field: keyof Address; from: string; to: string }[];
  issues: { code: AddressIssueCode; message: string }[];
  /** Vorschläge für die manuelle Prüfung */
  suggestions: { street: string; zip: string; city: string; score: number }[];
  leitcode?: string;
  dataVersion: string;
  reason?: string;
}

export const addressCheckSettingsSchema = z.object({
  /**
   * logiful = Aufträge mit Klärungsbedarf hält Logiful zurück und korrigiert (Standard),
   * transfer = trotzdem übertragen (automatische Korrekturen gelten, Rest unverändert),
   * customer = zurückhalten und den Kunden selbst korrigieren lassen
   */
  mode: z.enum(["logiful", "transfer", "customer"]).default("logiful"),
  /** eindeutige Korrekturen automatisch übernehmen */
  autoCorrect: z.boolean().default(true),
  enabled: z.boolean().default(true),
});
export type AddressCheckSettings = z.infer<typeof addressCheckSettingsSchema>;

/** ab dieser Ähnlichkeit gilt ein Straßentreffer als sicher (wenn eindeutig) */
const SURE = 0.9;
const SUGGEST = 0.78;

const PACKSTATION = /\b(?:packstation|pack-station|paketstation|ps)\s*(?:nr\.?|no\.?|#)?\s*[:.-]?\s*(\d{3})\b/i;
const FILIALE = /\b(?:post\s*-?filiale|postfiliale|filiale|postfil\.?)\s*(?:nr\.?|#)?\s*[:.-]?\s*(\d{3,4})\b/i;
const POSTNUMMER = /\b(\d{6,10})\b/;

export async function checkAddress(input: Address, ref: AddressReference): Promise<AddressCheckResult> {
  const base = { changes: [] as AddressCheckResult["changes"], issues: [] as AddressCheckResult["issues"], suggestions: [] as AddressCheckResult["suggestions"], dataVersion: ref.version };
  if ((input.country ?? "").toUpperCase() !== "DE") return { ...base, status: "skipped", kind: "street", reason: "nur Inlandsadressen" };
  const issue = (code: AddressIssueCode) => base.issues.push({ code, message: ADDRESS_ISSUES[code] });

  const lines = [input.street, input.houseNumber ?? "", input.addition ?? "", input.company ?? ""].join(" ");
  const ps = PACKSTATION.exec(lines);
  const fil = ps ? null : FILIALE.exec(lines);
  if (ps || fil) return checkDropPoint(input, ref, ps ? "packstation" : "postfiliale", Number((ps ?? fil)![1]), base, issue);

  const out: Address = { ...input };
  const change = (field: keyof Address, to: string) => {
    const from = String(input[field] ?? "");
    if (from === to) return;
    base.changes.push({ field, from, to });
    (out as unknown as Record<string, string>)[field] = to;
  };

  const split = splitStreet(input.street, input.houseNumber);
  const hn = split.houseNumber;
  if (!hn) issue("house_number_missing");
  const zip = (input.zip ?? "").replace(/\D/g, "");

  // 1. Straße in der angegebenen PLZ suchen, sonst in allen PLZ des Ortes
  const inZip = zip.length === 5 ? await ref.streets(zip) : null;
  type Cand = { plz: string; street: RefStreet; score: number };
  const rank = (plz: string, list: RefStreet[]) => list.map((street) => ({ plz, street, score: streetSimilarity(split.street, street.name) })).filter((c) => c.score >= SUGGEST);
  const fits = (c: Cand) => !hn || c.street.sections.some((s) => sectionContains(s, hn));
  const cands: Cand[] = inZip ? rank(zip, inZip) : [];
  // Erweitern, wenn die Straße in der PLZ fehlt oder die Hausnummer dort nicht liegt (lange Straßen über mehrere PLZ)
  if (!cands.some((c) => c.score >= SURE && fits(c))) {
    const cityNames = new Set([input.city ?? "", ...(zip.length === 5 ? (await ref.localitiesByPlz(zip)).filter((l) => l.kind === "place").map((l) => l.officialName) : [])]);
    const byCity = (await Promise.all([...cityNames].filter(Boolean).map((c) => ref.localitiesByCity(c)))).flat();
    const plzs = [...new Set(byCity.map((l) => l.plz))].filter((p) => p !== zip).slice(0, 60);
    for (const p of plzs) cands.push(...rank(p, (await ref.streets(p)) ?? []));
  }
  if (!inZip && zip.length === 5 && !cands.length) issue("zip_unknown");

  // archivierte Straßen auf die neue Straße umlenken
  const resolved: Cand[] = [];
  for (const c of cands) {
    if (c.street.status === "G") { resolved.push(c); continue; }
    const target = c.street.redirectKey ? ((await ref.streets(c.plz)) ?? []).find((s) => s.key === c.street.redirectKey && s.status === "G") : undefined;
    if (target) resolved.push({ plz: c.plz, street: target, score: c.score });
  }
  // Abschnitt zur Hausnummer bevorzugen: dieselbe Straße kann je Hausnummer in verschiedenen PLZ liegen
  resolved.sort((a, b) => b.score - a.score || Number(fits(b)) - Number(fits(a)) || Number(b.plz === zip) - Number(a.plz === zip));
  const best = resolved[0];
  const sameStreet = (a: Cand, b: Cand) => a.street.key === b.street.key || normalizeStreet(a.street.name) === normalizeStreet(b.street.name);
  const rivals = best ? resolved.filter((c) => !sameStreet(c, best) && c.score > best.score - 0.04) : [];

  const suggest = async () => {
    const seen = new Set<string>();
    for (const c of resolved) {
      const id = `${c.plz}|${c.street.name}`;
      if (seen.has(id) || seen.size >= 5) continue;
      seen.add(id);
      const loc = (await ref.localitiesByPlz(c.plz)).find((l) => l.alort === c.street.alort && l.kind === "place");
      base.suggestions.push({ street: c.street.name, zip: c.plz, city: loc?.officialName ?? input.city, score: Math.round(c.score * 100) / 100 });
    }
  };

  if (!best || best.score < SURE) {
    issue("street_unknown");
    await suggest();
    return { ...base, status: "manual", kind: "street" };
  }
  if (rivals.length) {
    issue("street_ambiguous");
    await suggest();
    return { ...base, status: "manual", kind: "street" };
  }

  // 2. Hausnummer im Bereich? Sonst dieselbe Straße in anderer PLZ des Ortes suchen
  let target = best;
  if (hn && !fits(best)) {
    const other = resolved.find((c) => sameStreet(c, best) && fits(c));
    if (other) target = other;
    else issue("house_number_unknown");
  }
  const section = hn ? target.street.sections.find((s) => sectionContains(s, hn)) : target.street.sections[0];

  // 3. Korrekturen: Straße (offizielle Schreibweise), Hausnummer getrennt, PLZ, Ort
  // nur inhaltliche Änderungen zählen – "Hauptstraße" bleibt neben offiziell "Hauptstr." stehen
  if (normalizeStreet(split.street) !== normalizeStreet(target.street.name)) {
    change("street", input.houseNumber ? target.street.name : `${target.street.name}${hn ? ` ${hn.raw}` : ""}`);
    // Text hinter der Hausnummer ("Hinterhaus") nicht verlieren
    if (split.rest && !input.houseNumber) change("addition", [input.addition, split.rest].filter(Boolean).join(", "));
  }
  if (target.plz !== zip) change("zip", target.plz);
  const locs = await ref.localitiesByPlz(target.plz);
  const official = locs.find((l) => l.alort === target.street.alort && l.kind === "place") ?? locs.find((l) => l.kind === "place");
  if (official) {
    const cityOk = locs.some((l) => citySimilarity(input.city ?? "", l.name) >= 0.97);
    if (!cityOk || normalizeCity(input.city ?? "") !== normalizeCity(official.officialName)) {
      // Ortsteil statt Ort ist zulässig, wird aber auf den postalischen Ort gesetzt
      change("city", official.officialName);
    }
  }

  if (base.issues.length) {
    await suggest();
    return { ...base, status: "manual", kind: "street" };
  }
  return {
    ...base,
    status: base.changes.length ? "corrected" : "ok",
    kind: "street",
    address: out,
    ...(section && hn ? { leitcode: leitcode(target.plz, section.code, hn.number) } : {}),
  };
}

async function checkDropPoint(
  input: Address,
  ref: AddressReference,
  kind: "packstation" | "postfiliale",
  number: number,
  base: Pick<AddressCheckResult, "changes" | "issues" | "suggestions" | "dataVersion">,
  issue: (c: AddressIssueCode) => void,
): Promise<AddressCheckResult> {
  // Postnummer: Adresszusatz, Firma oder Name (aber nicht die Stationsnummer selbst)
  const fields = [input.addition, input.company, input.firstName, input.lastName, input.street, input.houseNumber].filter(Boolean).join(" ");
  const postnummer = POSTNUMMER.exec(fields.replace(new RegExp(`\\b${number}\\b`), " "))?.[1];
  if (!postnummer) issue("postnummer_missing");

  const zip = (input.zip ?? "").replace(/\D/g, "");
  const plzs = kind === "packstation" ? await ref.packstationPlz(number) : await ref.filialePlz(number);
  let plz: string | null = plzs.includes(zip) ? zip : null;
  if (!plz && plzs.length) {
    // gleiche Nummer gibt es in vielen Orten – nur eindeutig über den Ort korrigieren
    const matches: string[] = [];
    for (const p of plzs) if ((await ref.localitiesByPlz(p)).some((l) => citySimilarity(input.city ?? "", l.name) >= 0.92)) matches.push(p);
    if (matches.length === 1) plz = matches[0]!;
  }
  if (!plz) {
    issue(kind === "packstation" ? "packstation_unknown" : "filiale_unknown");
    return { ...base, status: "manual", kind };
  }

  const out: Address = { ...input };
  const set = (field: keyof Address, to: string | undefined) => {
    const from = String(input[field] ?? "");
    if (from === (to ?? "")) return;
    base.changes.push({ field, from, to: to ?? "" });
    if (to === undefined) delete (out as unknown as Record<string, unknown>)[field];
    else (out as unknown as Record<string, string>)[field] = to;
  };
  // DHL-Schreibweise: Straße "Packstation"/"Postfiliale", Hausnummer = Stationsnummer, Postnummer im Adresszusatz
  const label = kind === "packstation" ? "Packstation" : "Postfiliale";
  // "Packstation 123" in einem Feld ist gleichwertig zu Straße + Nummer – nur abweichende Schreibweisen umstellen
  const sameStation = `${input.street} ${input.houseNumber ?? ""}`.toLowerCase().replace(/\s+/g, " ").trim() ===`${label} ${number}`.toLowerCase();
  if (!sameStation) {
    set("street", label);
    set("houseNumber", String(number));
  }
  if (postnummer) set("addition", postnummer);
  if (plz !== zip) set("zip", plz);
  const official = (await ref.localitiesByPlz(plz)).find((l) => l.kind === "place");
  if (official && normalizeCity(input.city ?? "") !== normalizeCity(official.officialName)) set("city", official.officialName);
  // Firma, die nur die Station enthielt ("Packstation 123"), entfernen
  if (input.company && (PACKSTATION.test(input.company) || FILIALE.test(input.company)) && input.company.replace(PACKSTATION, "").replace(FILIALE, "").trim().length < 3) set("company", undefined);

  if (base.issues.length) return { ...base, status: "manual", kind };
  return { ...base, status: base.changes.length ? "corrected" : "ok", kind, address: out };
}

